import type { TFunction } from 'i18next';
import { isApiRequestError, isNetworkError, isOfflineError } from '../api/errors.js';
import { formatWait } from './use-countdown.js';
import { retryAfterSeconds } from './auth-contract.js';

/**
 * One sentence for whatever just went wrong, and it always says what to do next.
 *
 * This is where trust is won or lost. A person who mistypes a PIN on a shop counter needs to
 * be told they have thirty seconds to wait, not "too many attempts" with no end in sight — the
 * second one reads as a permanent ban and ends in a phone call to the owner.
 *
 * The API's own refusals are already written for a human ("That email address and password do
 * not match"), so they are shown verbatim rather than re-translated into something vaguer
 * here. Only the two cases the server cannot describe — a dead connection, and a lockout whose
 * remaining time must be rendered in the reader's own units — get local copy.
 */
export function authErrorMessage(error: unknown, t: TFunction): string {
  if (isOfflineError(error) || isNetworkError(error)) return t('error.network');

  if (isApiRequestError(error)) {
    const wait = retryAfterSeconds(error);
    if (error.status === 429 && wait !== null) {
      return t('auth.lockedOut', { wait: formatWait(wait) });
    }
    // 500s are the one server answer that is not written for a shop owner to read.
    if (error.status >= 500) return t('error.unexpected');
    return error.message;
  }

  return t('error.unexpected');
}
